/**
 * UNIFIED BOOKING TABLE - Single Source of Truth Component
 * =========================================================
 * This is THE booking table for Pipeline X. Used across:
 * - Sales Control (Rep/Manager view)
 * - BD Revenue Tracker
 * - Founder Verification Sheet
 * 
 * Props:
 * - role: 'rep' | 'manager' | 'city_head' | 'bd' | 'admin'
 * - defaultFilters: { status, manager_id, rep_id, etc. }
 * - columnsVisible: array of column keys to show
 * - onRowClick: callback when row is clicked
 */

import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Badge } from '../components/ui/badge';
import { 
  Select, 
  SelectContent, 
  SelectItem, 
  SelectTrigger, 
  SelectValue 
} from '../components/ui/select';
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
} from '../components/ui/sheet';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '../components/ui/tooltip';
import { 
  Search, Calendar, Filter, ChevronLeft, ChevronRight, 
  AlertTriangle, Clock, CheckCircle2, XCircle, 
  ArrowRight, Eye, EyeOff, Download, Flag,
  Building2, User, Users, IndianRupee, FileText,
  MoreVertical, History, MessageSquare, FileSpreadsheet, Paperclip,
  Pencil, Check, X, Upload, Loader2, Info, Receipt
} from 'lucide-react';
import { toast } from 'sonner';
import api from '../utils/api';

// ============================================================================
// BOOKING STATUS SYSTEM - SINGLE SOURCE OF TRUTH
// ============================================================================
// All bookings use canonical status values. Legacy statuses have been migrated.

// Official booking statuses (matches backend BookingStatus class)
const BOOKING_STATUSES = {
  // Verification Flow
  SUBMITTED: { label: 'Submitted', color: 'bg-blue-100 text-blue-700', order: 1, group: 'pending' },
  QUERY_RAISED: { label: 'Query Raised', color: 'bg-red-100 text-red-700', order: 0, group: 'action_needed' },
  CITY_HEAD_VERIFIED: { label: 'City Verified', color: 'bg-cyan-100 text-cyan-700', order: 2, group: 'verified' },
  CRM_VERIFIED: { label: 'CRM Approved', color: 'bg-emerald-100 text-emerald-700', order: 3, group: 'approved' },
  STRATEGY_VERIFIED: { label: 'Approved', color: 'bg-emerald-100 text-emerald-700', order: 3, group: 'approved' },  // Legacy - maps to CRM_VERIFIED
  
  // Post-Approval Flow
  AGREEMENT_DONE: { label: 'Agreement Done', color: 'bg-purple-100 text-purple-700', order: 4, group: 'approved' },
  REGISTRATION_DONE: { label: 'Registered', color: 'bg-indigo-100 text-indigo-700', order: 5, group: 'approved' },
  INVOICE_RAISED: { label: 'Invoice Raised', color: 'bg-amber-100 text-amber-700', order: 6, group: 'approved' },
  REVENUE_RECEIVED: { label: 'Revenue Received', color: 'bg-green-100 text-green-700', order: 7, group: 'approved' },
  
  // Cancelled States (both show as "Cancelled" - simplified label)
  CANCELLED_PRE_VERIFICATION: { label: 'Cancelled', color: 'bg-red-100 text-red-700', order: -1, group: 'cancelled' },
  CANCELLED_POST_VERIFICATION: { label: 'Cancelled', color: 'bg-red-100 text-red-700', order: -2, group: 'cancelled' },
};

// Get status config with fallback
const getStatusConfig = (status) => {
  return BOOKING_STATUSES[status] || { label: status || 'Unknown', color: 'bg-slate-100 text-slate-600', order: 0, group: 'unknown' };
};

// Queue-based filter groups
const QUEUE_FILTERS = [
  { key: 'all', label: 'All', statuses: [] },
  { key: 'submitted', label: 'Submitted', statuses: ['SUBMITTED'] },
  { key: 'query_raised', label: 'Query Raised', statuses: ['QUERY_RAISED'] },
  { key: 'city_review', label: 'City Review', statuses: ['SUBMITTED', 'QUERY_RAISED'] },
  { key: 'crm_review', label: 'CRM Review', statuses: ['CITY_HEAD_VERIFIED'] },
  { key: 'crm_verified', label: 'CRM Verified', statuses: ['CRM_VERIFIED', 'STRATEGY_VERIFIED', 'AGREEMENT_DONE', 'REGISTRATION_DONE', 'INVOICE_RAISED', 'REVENUE_RECEIVED'] },
  { key: 'cancelled', label: 'Cancelled', statuses: ['CANCELLED_PRE_VERIFICATION', 'CANCELLED_POST_VERIFICATION'] },
];

// Action Required logic - determines who needs to act on the booking
// This is the CRM's daily work queue
const getActionRequired = (booking) => {
  const status = booking.status;
  
  // Query Raised - Rep needs to act
  if (status === 'QUERY_RAISED') {
    return { label: 'Rep Action Needed', color: 'bg-red-100 text-red-700 border-red-200', icon: AlertTriangle, priority: 1 };
  }
  
  // Submitted - City Head needs to review
  if (status === 'SUBMITTED') {
    return { label: 'City Head Review', color: 'bg-blue-100 text-blue-700 border-blue-200', icon: Clock, priority: 2 };
  }
  
  // City Head Verified - CRM needs to verify
  if (status === 'CITY_HEAD_VERIFIED') {
    return { label: 'CRM Verification', color: 'bg-amber-100 text-amber-700 border-amber-200', icon: Clock, priority: 3 };
  }
  
  // CRM Verified - Agreement pending
  if (status === 'CRM_VERIFIED' || status === 'STRATEGY_VERIFIED') {
    return { label: 'Agreement Pending', color: 'bg-purple-100 text-purple-700 border-purple-200', icon: FileText, priority: 4 };
  }
  
  // Agreement Done - Registration pending
  if (status === 'AGREEMENT_DONE') {
    return { label: 'Registration Pending', color: 'bg-orange-100 text-orange-700 border-orange-200', icon: Building2, priority: 5 };
  }
  
  // Registration Done - Invoice pending
  if (status === 'REGISTRATION_DONE') {
    return { label: 'Invoice Pending', color: 'bg-indigo-100 text-indigo-700 border-indigo-200', icon: Receipt, priority: 6 };
  }
  
  // Invoice Raised - Revenue pending
  if (status === 'INVOICE_RAISED') {
    return { label: 'Revenue Pending', color: 'bg-emerald-100 text-emerald-700 border-emerald-200', icon: IndianRupee, priority: 7 };
  }
  
  // Revenue Received - Complete
  if (status === 'REVENUE_RECEIVED') {
    return { label: 'Complete', color: 'bg-green-100 text-green-700 border-green-200', icon: CheckCircle2, priority: 99 };
  }
  
  // Cancelled - closed
  if (status?.includes('CANCELLED')) {
    return { label: 'Cancelled', color: 'bg-red-50 text-red-500 border-red-200', icon: XCircle, priority: 100 };
  }
  
  return { label: 'Unknown', color: 'bg-slate-50 text-slate-400 border-slate-200', icon: Clock, priority: 50 };
};

// Action badge colors based on type
const ACTION_BADGE_COLORS = {
  action: 'bg-amber-100 text-amber-700 border-amber-200',
  closed: 'bg-slate-100 text-slate-500 border-slate-200',
  unknown: 'bg-slate-100 text-slate-500 border-slate-200',
};

// Status flow for progress tracker
const STATUS_FLOW = [
  'SUBMITTED', 'CITY_HEAD_VERIFIED', 'CRM_VERIFIED', 
  'AGREEMENT_DONE', 'REGISTRATION_DONE', 'INVOICE_RAISED', 'REVENUE_RECEIVED'
];

// "Stuck" thresholds (in days)
const STUCK_THRESHOLDS = {
  SUBMITTED: 3,
  CITY_HEAD_VERIFIED: 5,
  CRM_VERIFIED: 7,
  AGREEMENT_DONE: 10,
  REGISTRATION_DONE: 10,
  INVOICE_RAISED: 5,
};

// === STATUS PROGRESS TRACKER COMPONENT ===
// === STATUS PROGRESS TRACKER (IMPROVED WITH LABELS) ===
// Shows: Submitted → City Verified → CRM Verified → Agreement → Registration → Invoice → Revenue
const StatusProgressTracker = ({ currentStatus, isCancelled, showLabels = false }) => {
  if (isCancelled || currentStatus?.includes('CANCELLED')) {
    return (
      <div className="flex items-center gap-1">
        <XCircle size={14} className="text-red-500" />
        <span className="text-xs text-red-600 font-medium">Cancelled</span>
      </div>
    );
  }
  
  // Query Raised shows warning
  if (currentStatus === 'QUERY_RAISED') {
    return (
      <div className="flex items-center gap-1">
        <AlertTriangle size={14} className="text-red-500" />
        <span className="text-xs text-red-600 font-medium">Query Raised</span>
      </div>
    );
  }
  
  const currentOrder = BOOKING_STATUSES[currentStatus]?.order || 0;
  
  // Short labels for the progress tracker
  const STAGE_LABELS = {
    'SUBMITTED': 'Submitted',
    'CITY_HEAD_VERIFIED': 'City',
    'CRM_VERIFIED': 'CRM',
    'AGREEMENT_DONE': 'Agreement',
    'REGISTRATION_DONE': 'Regn',
    'INVOICE_RAISED': 'Invoice',
    'REVENUE_RECEIVED': 'Revenue'
  };
  
  // If showLabels is true, show full progress bar with labels
  if (showLabels) {
    return (
      <div className="flex items-center gap-1 text-[10px]">
        {STATUS_FLOW.map((status, idx) => {
          const statusOrder = BOOKING_STATUSES[status]?.order || 0;
          const isCompleted = currentOrder >= statusOrder;
          const isCurrent = currentStatus === status || 
                           (currentStatus === 'STRATEGY_VERIFIED' && status === 'CRM_VERIFIED');
          
          return (
            <React.Fragment key={status}>
              <div className={`flex items-center gap-0.5 px-1 py-0.5 rounded ${
                isCurrent 
                  ? 'bg-emerald-100 text-emerald-700 font-semibold' 
                  : isCompleted 
                    ? 'text-emerald-600' 
                    : 'text-slate-400'
              }`}>
                <div className={`w-1.5 h-1.5 rounded-full ${
                  isCompleted ? 'bg-emerald-500' : 'bg-slate-300'
                }`} />
                <span>{STAGE_LABELS[status]}</span>
              </div>
              {idx < STATUS_FLOW.length - 1 && (
                <span className={isCompleted ? 'text-emerald-400' : 'text-slate-300'}>→</span>
              )}
            </React.Fragment>
          );
        })}
      </div>
    );
  }
  
  // Default compact view with dots
  return (
    <div className="flex items-center gap-0.5">
      {STATUS_FLOW.map((status, idx) => {
        const statusOrder = BOOKING_STATUSES[status]?.order || 0;
        const isCompleted = currentOrder >= statusOrder;
        const isCurrent = currentStatus === status || 
                         (currentStatus === 'STRATEGY_VERIFIED' && status === 'CRM_VERIFIED');
        
        return (
          <React.Fragment key={status}>
            <div 
              className={`w-2 h-2 rounded-full transition-all ${
                isCompleted 
                  ? isCurrent 
                    ? 'bg-emerald-500 ring-2 ring-emerald-200' 
                    : 'bg-emerald-500'
                  : 'bg-slate-200'
              }`}
              title={BOOKING_STATUSES[status]?.label}
            />
            {idx < STATUS_FLOW.length - 1 && (
              <div className={`w-3 h-0.5 ${isCompleted ? 'bg-emerald-500' : 'bg-slate-200'}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

// === STUCK INDICATOR COMPONENT ===
const StuckIndicator = ({ status, daysInStage }) => {
  const threshold = STUCK_THRESHOLDS[status];
  if (!threshold || daysInStage <= threshold) return null;
  
  const severity = daysInStage > threshold * 2 ? 'critical' : 'warning';
  
  return (
    <div className={`flex items-center gap-1 px-1.5 py-0.5 rounded text-xs ${
      severity === 'critical' 
        ? 'bg-red-100 text-red-700' 
        : 'bg-amber-100 text-amber-700'
    }`}>
      <AlertTriangle size={12} />
      <span>Stuck {daysInStage}d</span>
    </div>
  );
};

// === BOOKING DETAIL DRAWER COMPONENT ===
const BookingDetailDrawer = ({ booking, isOpen, onClose, userRole, onStatusUpdate, onRefresh }) => {
  const [auditLogs, setAuditLogs] = useState([]);
  const [loading, setLoading] = useState(false);
  const [escalationNote, setEscalationNote] = useState('');
  const [showRaiseQueryDialog, setShowRaiseQueryDialog] = useState(false);
  const [queryRemark, setQueryRemark] = useState('');
  const [isSubmittingQuery, setIsSubmittingQuery] = useState(false);
  const [isVerifying, setIsVerifying] = useState(false);
  
  // Cancel booking state
  const [showCancelDialog, setShowCancelDialog] = useState(false);
  const [cancelReason, setCancelReason] = useState('');
  const [cancelRemarks, setCancelRemarks] = useState('');
  const [isCancelling, setIsCancelling] = useState(false);
  
  // Cancellation reason categories for analytics
  const CANCEL_REASONS = [
    'Customer decision',
    'Loan rejection',
    'Builder issue',
    'Documentation issue',
    'Duplicate booking',
    'Pricing mismatch',
    'Property issue',
    'Other'
  ];
  
  useEffect(() => {
    if (isOpen && booking) {
      // Fetch audit logs for this booking
      // For now, we'll show placeholder
      setAuditLogs([]);
    }
  }, [isOpen, booking]);
  
  if (!booking) return null;
  
  const isCancelled = booking.status?.includes('CANCELLED');
  const isCrmSupport = userRole === 'crm_support';
  const canUpdate = userRole === 'admin' || userRole === 'super_admin' || userRole === 'city_head' || userRole === 'crm_support';
  
  // City Head can verify SUBMITTED bookings
  const canCityVerify = ['SUBMITTED', 'QUERY_RAISED', 'Pending', 'Query Raised'].includes(booking.status);
  
  // CRM Support can verify CITY_HEAD_VERIFIED bookings (final approval)
  const canCrmVerify = (userRole === 'crm_support' || userRole === 'super_admin') && 
                       booking.status === 'CITY_HEAD_VERIFIED';
  
  // Can raise query on SUBMITTED or CITY_HEAD_VERIFIED
  const canRaiseQuery = ['SUBMITTED', 'CITY_HEAD_VERIFIED', 'Pending'].includes(booking.status);
  
  // CRM Support or Super Admin can cancel bookings
  const canCancel = (userRole === 'crm_support' || userRole === 'super_admin' || userRole === 'admin') && !isCancelled;
  
  const handleCancelBooking = async () => {
    if (!cancelReason) {
      toast.error('Please select a cancellation reason');
      return;
    }
    
    setIsCancelling(true);
    try {
      await api.cancelBooking(booking.booking_id, {
        cancel_reason: cancelReason,
        cancellation_source: 'crm_team',
        remarks: cancelRemarks
      });
      
      toast.success('Booking cancelled successfully');
      setShowCancelDialog(false);
      setCancelReason('');
      setCancelRemarks('');
      if (onRefresh) onRefresh();
      onClose();
    } catch (error) {
      console.error('Cancel error:', error);
      toast.error(error.response?.data?.detail || 'Failed to cancel booking');
    } finally {
      setIsCancelling(false);
    }
  };
  
  const handleEscalate = async () => {
    if (!escalationNote.trim()) {
      toast.error('Please add a note for escalation');
      return;
    }
    try {
      // Call escalation API
      toast.success('Escalation logged');
      setEscalationNote('');
    } catch (error) {
      toast.error('Failed to escalate');
    }
  };
  
  // Handle verify booking (City Head approval)
  const handleVerifyBooking = async () => {
    setIsVerifying(true);
    try {
      await api.bookingVerifyAction(booking.booking_id, 'city_verify');
      toast.success('Booking verified successfully');
      onRefresh?.();
      onClose();
    } catch (error) {
      console.error('Failed to verify booking:', error);
      toast.error(error.response?.data?.detail || 'Failed to verify booking');
    } finally {
      setIsVerifying(false);
    }
  };
  
  // Handle CRM Verification (final approval)
  const handleCrmVerify = async () => {
    setIsVerifying(true);
    try {
      await api.bookingVerifyAction(booking.booking_id, 'crm_approve');
      toast.success('Booking approved by CRM. Now eligible for Net Sales.');
      onRefresh?.();
      onClose();
    } catch (error) {
      console.error('Failed to approve booking:', error);
      toast.error(error.response?.data?.detail || 'Failed to approve booking');
    } finally {
      setIsVerifying(false);
    }
  };
  
  // Handle raise query
  const handleRaiseQuery = async () => {
    if (!queryRemark.trim()) {
      toast.error('Please enter a query remark');
      return;
    }
    
    setIsSubmittingQuery(true);
    try {
      await api.bookingVerifyAction(booking.booking_id, 'raise_query', queryRemark);
      toast.success('Query raised successfully. Rep has been notified.');
      setQueryRemark('');
      setShowRaiseQueryDialog(false);
      onRefresh?.();
      onClose();
    } catch (error) {
      console.error('Failed to raise query:', error);
      toast.error(error.response?.data?.detail || 'Failed to raise query');
    } finally {
      setIsSubmittingQuery(false);
    }
  };
  
  return (
    <Sheet open={isOpen} onOpenChange={onClose}>
      <SheetContent className="w-[450px] sm:w-[540px] overflow-y-auto">
        <SheetHeader>
          <SheetTitle className="flex items-center gap-2">
            <FileText size={20} />
            Booking Details
          </SheetTitle>
        </SheetHeader>
        
        <div className="mt-6 space-y-6">
          {/* Booking ID & Status */}
          <div className="flex items-center justify-between">
            <div>
              <p className="text-xs text-slate-500">Booking ID</p>
              <p className="font-mono text-sm font-medium">{booking.booking_id}</p>
            </div>
            <Badge className={BOOKING_STATUSES[booking.status]?.color || 'bg-slate-100'}>
              {BOOKING_STATUSES[booking.status]?.label || booking.status}
            </Badge>
          </div>
          
          {/* Progress Tracker with Clear Labels */}
          <div className="p-4 bg-slate-50 rounded-xl">
            <p className="text-xs text-slate-500 mb-3">Lifecycle Progress</p>
            <div className="flex items-center justify-between overflow-x-auto">
              {STATUS_FLOW.map((status, idx) => {
                const statusOrder = BOOKING_STATUSES[status]?.order || 0;
                const currentOrder = BOOKING_STATUSES[booking.status]?.order || 0;
                const isCompleted = currentOrder >= statusOrder;
                const isCurrent = booking.status === status || 
                                 (booking.status === 'STRATEGY_VERIFIED' && status === 'CRM_VERIFIED');
                
                // Shorter labels for the tracker
                const SHORT_LABELS = {
                  'SUBMITTED': 'Submitted',
                  'CITY_HEAD_VERIFIED': 'City',
                  'CRM_VERIFIED': 'CRM',
                  'AGREEMENT_DONE': 'Agreement',
                  'REGISTRATION_DONE': 'Regn',
                  'INVOICE_RAISED': 'Invoice',
                  'REVENUE_RECEIVED': 'Revenue'
                };
                
                return (
                  <React.Fragment key={status}>
                    <div className="flex flex-col items-center">
                      <div 
                        className={`w-7 h-7 rounded-full flex items-center justify-center text-xs ${
                          isCompleted 
                            ? isCurrent 
                              ? 'bg-emerald-500 text-white ring-4 ring-emerald-100' 
                              : 'bg-emerald-500 text-white'
                            : 'bg-slate-200 text-slate-400'
                        }`}
                      >
                        {isCompleted ? <CheckCircle2 size={14} /> : idx + 1}
                      </div>
                      <p className={`text-[10px] mt-1 text-center whitespace-nowrap ${isCurrent ? 'font-bold text-emerald-700' : isCompleted ? 'text-emerald-600' : 'text-slate-400'}`}>
                        {SHORT_LABELS[status]}
                      </p>
                    </div>
                    {idx < STATUS_FLOW.length - 1 && (
                      <ArrowRight size={12} className={`mx-0.5 flex-shrink-0 ${isCompleted ? 'text-emerald-500' : 'text-slate-300'}`} />
                    )}
                  </React.Fragment>
                );
              })}
            </div>
          </div>
          
          {/* Customer & Project Info */}
          <div className="grid grid-cols-2 gap-4">
            <div className="p-3 bg-white border rounded-xl">
              <div className="flex items-center gap-2 text-slate-500 mb-1">
                <User size={14} />
                <span className="text-xs">Customer</span>
              </div>
              <p className="font-medium text-sm">{booking.customer_name || 'N/A'}</p>
            </div>
            <div className="p-3 bg-white border rounded-xl">
              <div className="flex items-center gap-2 text-slate-500 mb-1">
                <Building2 size={14} />
                <span className="text-xs">Project</span>
              </div>
              <p className="font-medium text-sm">{booking.project_name}</p>
            </div>
          </div>
          
          {/* Rep & Manager */}
          <div className="grid grid-cols-2 gap-4">
            <div className="p-3 bg-white border rounded-xl">
              <div className="flex items-center gap-2 text-slate-500 mb-1">
                <User size={14} />
                <span className="text-xs">Sales Rep</span>
              </div>
              <p className="font-medium text-sm">{booking.rep_name}</p>
            </div>
            <div className="p-3 bg-white border rounded-xl">
              <div className="flex items-center gap-2 text-slate-500 mb-1">
                <Users size={14} />
                <span className="text-xs">Manager</span>
              </div>
              <p className="font-medium text-sm">{booking.manager_name || 'N/A'}</p>
            </div>
          </div>
          
          {/* Financial Summary */}
          <div className="p-4 bg-gradient-to-r from-emerald-50 to-cyan-50 rounded-xl">
            <div className="flex items-center gap-2 text-slate-600 mb-2">
              <IndianRupee size={14} />
              <span className="text-xs font-medium">Agreement Value</span>
            </div>
            <p className="text-2xl font-bold text-emerald-700">
              ₹{(booking.av_lacs || 0).toFixed(2)} L
            </p>
            <div className="flex items-center gap-4 mt-2 text-xs text-slate-500">
              <span>Type: {booking.booking_type || 'DIRECT'}</span>
              <span>•</span>
              <span>Date: {booking.booking_date}</span>
            </div>
          </div>
          
          {/* Stage Duration */}
          <div className="p-4 bg-white border rounded-xl">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Clock size={14} className="text-slate-500" />
                <span className="text-sm text-slate-600">Days in Current Stage</span>
              </div>
              <span className={`text-lg font-bold ${
                booking.days_in_current_stage > (STUCK_THRESHOLDS[booking.status] || 999) 
                  ? 'text-red-600' 
                  : 'text-slate-700'
              }`}>
                {booking.days_in_current_stage}
              </span>
            </div>
            {booking.days_in_current_stage > (STUCK_THRESHOLDS[booking.status] || 999) && (
              <p className="text-xs text-red-600 mt-2 flex items-center gap-1">
                <AlertTriangle size={12} />
                Exceeds typical processing time
              </p>
            )}
          </div>
          
          {/* Booking Actions - City Head / Admin / CRM Support */}
          {canUpdate && !isCancelled && (canCityVerify || canCrmVerify || canRaiseQuery) && (
            <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl" data-testid="booking-actions-section">
              <div className="flex items-center gap-2 mb-3">
                <CheckCircle2 size={14} className="text-blue-600" />
                <span className="text-sm font-medium text-blue-800">Booking Actions</span>
              </div>
              <div className="flex flex-col gap-2">
                {/* City Head Verify - for SUBMITTED/QUERY_RAISED bookings */}
                {canCityVerify && !isCrmSupport && (
                  <Button 
                    size="sm" 
                    className="w-full bg-cyan-600 hover:bg-cyan-700 text-white justify-start"
                    onClick={handleVerifyBooking}
                    disabled={isVerifying}
                    data-testid="city-verify-btn"
                  >
                    {isVerifying ? (
                      <Loader2 size={14} className="mr-2 animate-spin" />
                    ) : (
                      <CheckCircle2 size={14} className="mr-2" />
                    )}
                    City Head Verify
                  </Button>
                )}
                
                {/* CRM Verify - for CITY_HEAD_VERIFIED bookings (CRM Support only) */}
                {canCrmVerify && (
                  <Button 
                    size="sm" 
                    className="w-full bg-emerald-600 hover:bg-emerald-700 text-white justify-start"
                    onClick={handleCrmVerify}
                    disabled={isVerifying}
                    data-testid="crm-verify-btn"
                  >
                    {isVerifying ? (
                      <Loader2 size={14} className="mr-2 animate-spin" />
                    ) : (
                      <CheckCircle2 size={14} className="mr-2" />
                    )}
                    Verify Booking
                  </Button>
                )}
                
                {/* Raise Query - for SUBMITTED or CITY_HEAD_VERIFIED */}
                {canRaiseQuery && (
                  <Button 
                    size="sm" 
                    variant="outline"
                    className="w-full border-amber-300 text-amber-700 hover:bg-amber-50 justify-start"
                    onClick={() => setShowRaiseQueryDialog(true)}
                    data-testid="raise-query-btn"
                  >
                    <MessageSquare size={14} className="mr-2" />
                    Raise Query
                  </Button>
                )}
              </div>
              {booking.status === 'QUERY_RAISED' && (
                <p className="text-xs text-amber-600 mt-2 flex items-center gap-1">
                  <AlertTriangle size={12} />
                  Query already raised - waiting for rep to respond
                </p>
              )}
              {booking.status === 'CITY_HEAD_VERIFIED' && canCrmVerify && (
                <p className="text-xs text-emerald-600 mt-2 flex items-center gap-1">
                  <CheckCircle2 size={12} />
                  Ready for CRM verification. Once verified, booking is eligible for Net Sales.
                </p>
              )}
            </div>
          )}
          
          {/* Escalation Section (City Head Only) */}
          {userRole === 'city_head' && !isCancelled && (
            <div className="p-4 bg-amber-50 border border-amber-200 rounded-xl">
              <div className="flex items-center gap-2 mb-3">
                <Flag size={14} className="text-amber-600" />
                <span className="text-sm font-medium text-amber-800">Escalate Booking</span>
              </div>
              <Input
                placeholder="Add escalation note..."
                value={escalationNote}
                onChange={(e) => setEscalationNote(e.target.value)}
                className="mb-2"
              />
              <Button 
                size="sm" 
                variant="outline" 
                className="border-amber-300 text-amber-700 hover:bg-amber-100"
                onClick={handleEscalate}
              >
                <Flag size={14} className="mr-1" />
                Mark for Escalation
              </Button>
            </div>
          )}
          
          {/* Cancel Booking Section (CRM Support / Super Admin Only) */}
          {canCancel && (
            <div className="p-4 bg-red-50 border border-red-200 rounded-xl" data-testid="cancel-booking-section">
              <div className="flex items-center gap-2 mb-3">
                <XCircle size={14} className="text-red-600" />
                <span className="text-sm font-medium text-red-800">Cancel Booking</span>
              </div>
              <p className="text-xs text-red-600 mb-3">
                Only CRM Support can cancel bookings. This action is logged and cannot be undone.
              </p>
              <Button 
                size="sm" 
                variant="destructive"
                className="w-full"
                onClick={() => setShowCancelDialog(true)}
                data-testid="cancel-booking-btn"
              >
                <XCircle size={14} className="mr-2" />
                Cancel Booking
              </Button>
            </div>
          )}
          
          {/* Verification History - Shows who did what */}
          <div className="p-4 bg-slate-50 rounded-xl">
            <div className="flex items-center gap-2 mb-3">
              <History size={14} className="text-slate-500" />
              <span className="text-sm font-medium text-slate-700">Verification History</span>
            </div>
            <div className="space-y-3">
              {/* Submitted By */}
              <div className="flex items-center gap-3 text-sm">
                <div className="w-6 h-6 rounded-full bg-blue-100 flex items-center justify-center">
                  <User size={12} className="text-blue-600" />
                </div>
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className="text-slate-600">Submitted</span>
                    <span className="text-xs text-slate-400">→</span>
                    <span className="font-medium text-slate-800">{booking.rep_name || booking.submitted_by || 'Sales Rep'}</span>
                  </div>
                  <p className="text-xs text-slate-400">{booking.booking_date || booking.submitted_at || 'N/A'}</p>
                </div>
                <CheckCircle2 size={14} className="text-blue-500" />
              </div>
              
              {/* City Verified */}
              {(booking.status !== 'SUBMITTED' && booking.status !== 'QUERY_RAISED') && (
                <div className="flex items-center gap-3 text-sm">
                  <div className="w-6 h-6 rounded-full bg-cyan-100 flex items-center justify-center">
                    <User size={12} className="text-cyan-600" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-slate-600">City Verified</span>
                      <span className="text-xs text-slate-400">→</span>
                      <span className="font-medium text-slate-800">{booking.city_head_verified_by || booking.manager_name || 'City Head'}</span>
                    </div>
                    <p className="text-xs text-slate-400">{booking.city_head_verified_at || 'N/A'}</p>
                  </div>
                  <CheckCircle2 size={14} className="text-cyan-500" />
                </div>
              )}
              
              {/* CRM Verified */}
              {(['CRM_VERIFIED', 'STRATEGY_VERIFIED', 'AGREEMENT_DONE', 'REGISTRATION_DONE', 'INVOICE_RAISED', 'REVENUE_RECEIVED'].includes(booking.status)) && (
                <div className="flex items-center gap-3 text-sm">
                  <div className="w-6 h-6 rounded-full bg-emerald-100 flex items-center justify-center">
                    <User size={12} className="text-emerald-600" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-slate-600">CRM Verified</span>
                      <span className="text-xs text-slate-400">→</span>
                      <span className="font-medium text-slate-800">{booking.crm_verified_by || 'CRM Team'}</span>
                    </div>
                    <p className="text-xs text-slate-400">{booking.crm_verified_at || 'N/A'}</p>
                  </div>
                  <CheckCircle2 size={14} className="text-emerald-500" />
                </div>
              )}
              
              {/* Cancellation Info */}
              {booking.status?.includes('CANCELLED') && (
                <div className="flex items-center gap-3 text-sm">
                  <div className="w-6 h-6 rounded-full bg-red-100 flex items-center justify-center">
                    <XCircle size={12} className="text-red-600" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-slate-600">Cancelled</span>
                      <span className="text-xs text-slate-400">→</span>
                      <span className="font-medium text-slate-800">{booking.cancelled_by || 'CRM Team'}</span>
                    </div>
                    <p className="text-xs text-slate-400">{booking.cancelled_at || 'N/A'}</p>
                    {booking.cancellation_reason && (
                      <p className="text-xs text-red-600 mt-1">Reason: {booking.cancellation_reason}</p>
                    )}
                  </div>
                </div>
              )}
              
              {/* Query History */}
              {booking.status === 'QUERY_RAISED' && (
                <div className="flex items-center gap-3 text-sm">
                  <div className="w-6 h-6 rounded-full bg-amber-100 flex items-center justify-center">
                    <AlertTriangle size={12} className="text-amber-600" />
                  </div>
                  <div className="flex-1">
                    <div className="flex items-center gap-2">
                      <span className="text-slate-600">Query Raised</span>
                      <span className="text-xs text-slate-400">→</span>
                      <span className="font-medium text-slate-800">Awaiting Rep Response</span>
                    </div>
                    {booking.query_remark && (
                      <p className="text-xs text-amber-600 mt-1">Query: {booking.query_remark}</p>
                    )}
                  </div>
                </div>
              )}
            </div>
          </div>
          
          {/* Additional Booking Info */}
          <div className="p-4 bg-white border rounded-xl">
            <div className="flex items-center gap-2 mb-3">
              <Info size={14} className="text-slate-500" />
              <span className="text-sm font-medium text-slate-700">Additional Details</span>
            </div>
            <div className="grid grid-cols-2 gap-3 text-sm">
              <div>
                <p className="text-xs text-slate-500">City</p>
                <p className="font-medium">{booking.city || 'N/A'}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500">Unit No</p>
                <p className="font-medium">{booking.unit_no || 'N/A'}</p>
              </div>
              <div>
                <p className="text-xs text-slate-500">Token Amount</p>
                <p className="font-medium">₹{(booking.token_amount_lacs || 0).toFixed(2)} L</p>
              </div>
              <div>
                <p className="text-xs text-slate-500">Booking Date</p>
                <p className="font-medium">{booking.booking_date || 'N/A'}</p>
              </div>
            </div>
          </div>
        </div>
      </SheetContent>
      
      {/* Raise Query Dialog */}
      <Dialog open={showRaiseQueryDialog} onOpenChange={setShowRaiseQueryDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <MessageSquare size={20} />
              Raise Query
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-2">
            <div className="p-3 bg-slate-50 rounded-lg border">
              <p className="text-sm text-slate-600">
                <span className="font-medium">Booking:</span> {booking?.booking_id}
              </p>
              <p className="text-sm text-slate-600 mt-1">
                <span className="font-medium">Customer:</span> {booking?.customer_name}
              </p>
              <p className="text-sm text-slate-600 mt-1">
                <span className="font-medium">Rep:</span> {booking?.rep_name}
              </p>
            </div>
            
            <div>
              <label className="text-sm font-medium text-slate-700">
                Query Remark <span className="text-red-500">*</span>
              </label>
              <textarea
                value={queryRemark}
                onChange={(e) => setQueryRemark(e.target.value)}
                placeholder="Describe the issue or clarification needed..."
                className="w-full mt-1 p-3 border rounded-lg text-sm resize-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                rows={4}
                autoFocus
              />
              <p className="text-xs text-slate-400 mt-1">
                This will be visible to the rep and logged in query history
              </p>
            </div>
            
            <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
              <p className="text-xs text-amber-700">
                <strong>What happens next:</strong>
              </p>
              <ul className="text-xs text-amber-600 mt-1 space-y-1">
                <li>• Booking status will change to "Query Raised"</li>
                <li>• A task will be created for the rep</li>
                <li>• Rep will see this in their "My Bookings" and "My Tasks"</li>
              </ul>
            </div>
            
            <div className="flex gap-2 pt-2">
              <Button
                variant="outline"
                onClick={() => {
                  setShowRaiseQueryDialog(false);
                  setQueryRemark('');
                }}
                className="flex-1"
                disabled={isSubmittingQuery}
              >
                Cancel
              </Button>
              <Button
                onClick={handleRaiseQuery}
                disabled={!queryRemark.trim() || isSubmittingQuery}
                className="flex-1 bg-amber-600 hover:bg-amber-700"
                data-testid="confirm-raise-query-btn"
              >
                {isSubmittingQuery ? (
                  <>
                    <Loader2 size={16} className="mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <MessageSquare size={16} className="mr-2" />
                    Raise Query
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Cancel Booking Dialog (CRM Support Only) */}
      <Dialog open={showCancelDialog} onOpenChange={setShowCancelDialog}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <XCircle size={20} />
              Cancel Booking
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-2">
            <div className="p-3 bg-red-50 rounded-lg border border-red-200">
              <p className="text-sm text-red-700">
                <strong>Warning:</strong> This will mark the booking as cancelled. 
                The customer will be moved back to active pipeline if linked.
              </p>
            </div>
            
            <div className="p-3 bg-slate-50 rounded-lg border">
              <p className="text-sm text-slate-600">
                <span className="font-medium">Booking:</span> {booking?.booking_id}
              </p>
              <p className="text-sm text-slate-600 mt-1">
                <span className="font-medium">Customer:</span> {booking?.customer_name}
              </p>
              <p className="text-sm text-slate-600 mt-1">
                <span className="font-medium">Project:</span> {booking?.project_name}
              </p>
              <p className="text-sm text-slate-600 mt-1">
                <span className="font-medium">Agreement Value:</span> ₹{(booking?.agreement_value_lacs || booking?.av_lacs || 0).toFixed(2)} L
              </p>
            </div>
            
            <div>
              <label className="text-sm font-medium text-slate-700">
                Cancellation Reason <span className="text-red-500">*</span>
              </label>
              <Select value={cancelReason} onValueChange={setCancelReason}>
                <SelectTrigger className="mt-1">
                  <SelectValue placeholder="Select reason" />
                </SelectTrigger>
                <SelectContent>
                  {CANCEL_REASONS.map(reason => (
                    <SelectItem key={reason} value={reason}>{reason}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <div>
              <label className="text-sm font-medium text-slate-700">
                Additional Remarks
              </label>
              <textarea
                value={cancelRemarks}
                onChange={(e) => setCancelRemarks(e.target.value)}
                placeholder="Any additional details about the cancellation..."
                className="w-full mt-1 p-3 border rounded-lg text-sm resize-none focus:ring-2 focus:ring-red-500 focus:border-red-500"
                rows={3}
              />
            </div>
            
            <div className="flex gap-2 pt-2">
              <Button
                variant="outline"
                onClick={() => {
                  setShowCancelDialog(false);
                  setCancelReason('');
                  setCancelRemarks('');
                }}
                className="flex-1"
                disabled={isCancelling}
              >
                Back
              </Button>
              <Button
                variant="destructive"
                onClick={handleCancelBooking}
                disabled={!cancelReason || isCancelling}
                className="flex-1"
                data-testid="confirm-cancel-btn"
              >
                {isCancelling ? (
                  <>
                    <Loader2 size={16} className="mr-2 animate-spin" />
                    Cancelling...
                  </>
                ) : (
                  <>
                    <XCircle size={16} className="mr-2" />
                    Confirm Cancel
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </Sheet>
  );
};

// === MAIN COMPONENT ===
const UnifiedBookingTable = ({
  role = 'manager',
  defaultFilters = {},
  columnsVisible = ['actions', 'booking_date', 'customer_name', 'project_name', 'rep_name', 'av_lacs', 'token_amount', 'unit_no', 'documents', 'status', 'action_required'],
  onRowClick,
  title = 'Bookings',
  showFilters = true,
  showSummaryStrip = true,
  pageSize = 20,
}) => {
  const navigate = useNavigate();
  
  // Check if CRM Support - simplified view without analytics
  const isCrmSupport = role === 'crm_support';
  
  // State
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  
  // Filters
  const [period, setPeriod] = useState(defaultFilters.period || 'mtd');
  const [startDate, setStartDate] = useState(defaultFilters.startDate || '');
  const [endDate, setEndDate] = useState(defaultFilters.endDate || '');
  const [statusFilter, setStatusFilter] = useState(defaultFilters.status || '');
  const [queueFilter, setQueueFilter] = useState('all'); // New queue-based filter
  const [managerFilter, setManagerFilter] = useState(defaultFilters.manager_id || '');
  const [repFilter, setRepFilter] = useState(defaultFilters.rep_id || '');
  const [bookingTypeFilter, setBookingTypeFilter] = useState(defaultFilters.booking_type || '');
  const [searchQuery, setSearchQuery] = useState('');
  const [avMin, setAvMin] = useState(defaultFilters.av_min || '');
  const [avMax, setAvMax] = useState(defaultFilters.av_max || '');
  const [dateField, setDateField] = useState(defaultFilters.date_field || 'booking_date');  
  // Column visibility
  const [visibleColumns, setVisibleColumns] = useState(columnsVisible);
  const [showColumnPicker, setShowColumnPicker] = useState(false);
  
  // Drawer
  const [selectedBooking, setSelectedBooking] = useState(null);
  const [drawerOpen, setDrawerOpen] = useState(false);
  
  // Row actions state
  const [rowActionsBooking, setRowActionsBooking] = useState(null); // Booking for row actions menu
  const [showRowRaiseQueryDialog, setShowRowRaiseQueryDialog] = useState(false);
  const [rowQueryRemark, setRowQueryRemark] = useState('');
  const [isRowSubmittingQuery, setIsRowSubmittingQuery] = useState(false);
  const [isRowVerifying, setIsRowVerifying] = useState(false);
  
  // Inline editing state
  const [editingCell, setEditingCell] = useState(null); // { bookingId, field }
  const [editValue, setEditValue] = useState('');
  const [editReason, setEditReason] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [showEditReasonDialog, setShowEditReasonDialog] = useState(false);
  const [pendingEdit, setPendingEdit] = useState(null); // { bookingId, field, value }
  const fileInputRef = useRef(null);
  const [uploadingDoc, setUploadingDoc] = useState(null); // bookingId being uploaded to
  
  // Managers & Reps for filters
  const [managers, setManagers] = useState([]);
  const [reps, setReps] = useState([]);
  
  // Sync with defaultFilters when they change (e.g., from parent component)
  useEffect(() => {
    if (defaultFilters.status !== undefined) setStatusFilter(defaultFilters.status);
    if (defaultFilters.date_field !== undefined) setDateField(defaultFilters.date_field);
    if (defaultFilters.period !== undefined) setPeriod(defaultFilters.period);
    if (defaultFilters.manager_id !== undefined) setManagerFilter(defaultFilters.manager_id);
    if (defaultFilters.av_min !== undefined) setAvMin(defaultFilters.av_min);
  }, [defaultFilters.status, defaultFilters.date_field, defaultFilters.period, defaultFilters.manager_id, defaultFilters.av_min]);
  
  // Fetch managers and reps for filter dropdowns
  useEffect(() => {
    const fetchFilterOptions = async () => {
      try {
        // This would ideally come from a dedicated API
        const usersRes = await api.getTeamMembers();
        if (usersRes.data) {
          const managerList = usersRes.data.filter(u => ['Manager', 'city_admin'].includes(u.role));
          const repList = usersRes.data.filter(u => ['Sales', 'sales_rep'].includes(u.role));
          setManagers(managerList);
          setReps(repList);
        }
      } catch (error) {
        console.error('Failed to fetch filter options:', error);
      }
    };
    fetchFilterOptions();
  }, []);
  
  // Fetch bookings
  const fetchBookings = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      params.append('period', period);
      params.append('page', page.toString());
      params.append('limit', pageSize.toString());
      
      if (startDate && period === 'custom') params.append('start_date', startDate);
      if (endDate && period === 'custom') params.append('end_date', endDate);
      if (statusFilter) params.append('status', statusFilter);
      if (managerFilter) params.append('manager_id', managerFilter);
      if (repFilter) params.append('rep_id', repFilter);
      if (bookingTypeFilter) params.append('booking_type', bookingTypeFilter);
      if (avMin) params.append('av_min', avMin);
      if (avMax) params.append('av_max', avMax);
      if (dateField && dateField !== 'booking_date') params.append('date_field', dateField);
      
      const response = await api.getUnifiedBookingList(Object.fromEntries(params));
      
      let filteredBookings = response.data.bookings || [];
      
      // Client-side search filter
      if (searchQuery.trim()) {
        const query = searchQuery.toLowerCase();
        filteredBookings = filteredBookings.filter(b => 
          b.customer_name?.toLowerCase().includes(query) ||
          b.booking_id?.toLowerCase().includes(query) ||
          b.project_name?.toLowerCase().includes(query)
        );
      }
      
      setBookings(filteredBookings);
      setTotal(response.data.total || 0);
      setTotalPages(response.data.total_pages || 1);
      
      // Calculate summary stats for Dynamic Summary Strip
      const allBookings = response.data.bookings || [];
      
      // Calculate stats using canonical status field directly
      const stats = {
        total: allBookings.length,
        // Net AV: Only fully approved bookings (CRM_VERIFIED and beyond)
        netAv: allBookings.filter(b => 
          ['CRM_VERIFIED', 'STRATEGY_VERIFIED', 'AGREEMENT_DONE', 'REGISTRATION_DONE', 'INVOICE_RAISED', 'REVENUE_RECEIVED'].includes(b.status)
        ).reduce((sum, b) => sum + (b.av_lacs || 0), 0),
        // Individual status counts (mutually exclusive)
        submitted: allBookings.filter(b => b.status === 'SUBMITTED').length,
        queryRaised: allBookings.filter(b => b.status === 'QUERY_RAISED').length,
        cityVerified: allBookings.filter(b => b.status === 'CITY_HEAD_VERIFIED').length,
        approved: allBookings.filter(b => 
          ['CRM_VERIFIED', 'STRATEGY_VERIFIED', 'AGREEMENT_DONE', 'REGISTRATION_DONE', 'INVOICE_RAISED', 'REVENUE_RECEIVED'].includes(b.status)
        ).length,
        cancelled: allBookings.filter(b => 
          ['CANCELLED_PRE_VERIFICATION', 'CANCELLED_POST_VERIFICATION'].includes(b.status)
        ).length,
      };
      
      // Verify: sum of individual statuses should equal total
      const sumOfStatuses = stats.submitted + stats.queryRaised + stats.cityVerified + stats.approved + stats.cancelled;
      if (sumOfStatuses !== stats.total) {
        console.warn(`Status count mismatch: sum=${sumOfStatuses}, total=${stats.total}`);
      }
      
      setSummaryStats(stats);
    } catch (error) {
      console.error('Failed to fetch bookings:', error);
      toast.error('Failed to load bookings');
    } finally {
      setLoading(false);
    }
  }, [page, pageSize, period, startDate, endDate, statusFilter, managerFilter, repFilter, bookingTypeFilter, avMin, avMax, searchQuery, dateField]);
  
  // Summary stats state
  const [summaryStats, setSummaryStats] = useState({
    total: 0, netAv: 0, submitted: 0, queryRaised: 0, cityVerified: 0, approved: 0, cancelled: 0
  });
  
  useEffect(() => {
    fetchBookings();
  }, [fetchBookings]);
  
  // Handle row click
  const handleRowClick = (booking) => {
    // Don't navigate if we're in edit mode
    if (editingCell) return;
    
    if (onRowClick) {
      onRowClick(booking);
    } else {
      setSelectedBooking(booking);
      setDrawerOpen(true);
    }
  };
  
  // Start inline edit
  const startEdit = (e, bookingId, field, currentValue) => {
    e.stopPropagation();
    setEditingCell({ bookingId, field });
    setEditValue(currentValue || '');
  };
  
  // Cancel inline edit
  const cancelEdit = (e) => {
    e?.stopPropagation();
    setEditingCell(null);
    setEditValue('');
    setEditReason('');
    setPendingEdit(null);
    setShowEditReasonDialog(false);
  };
  
  // Submit inline edit - first show reason dialog
  const submitEdit = (e) => {
    e?.stopPropagation();
    if (!editingCell || !editValue) {
      cancelEdit();
      return;
    }
    
    // Find the booking to get old value
    const booking = bookings.find(b => b.booking_id === editingCell.bookingId);
    const oldValue = booking?.[editingCell.field === 'av_lacs' ? 'av_lacs' : editingCell.field];
    
    // Check if value actually changed
    if (String(editValue) === String(oldValue)) {
      cancelEdit();
      return;
    }
    
    setPendingEdit({
      bookingId: editingCell.bookingId,
      field: editingCell.field,
      value: editValue,
      oldValue
    });
    setShowEditReasonDialog(true);
  };
  
  // Confirm edit with reason
  const confirmEditWithReason = async () => {
    if (!pendingEdit || !editReason.trim()) {
      toast.error('Please provide a reason for the edit');
      return;
    }
    
    setIsSaving(true);
    try {
      const updateData = {
        edit_reason: editReason
      };
      
      // Map field names to API field names
      if (pendingEdit.field === 'av_lacs') {
        updateData.agreement_value_lacs = parseFloat(pendingEdit.value);
      } else if (pendingEdit.field === 'token_amount') {
        updateData.token_amount_lacs = parseFloat(pendingEdit.value);
      } else if (pendingEdit.field === 'unit_no') {
        updateData.unit_number = pendingEdit.value;
      }
      
      await api.inlineEditBooking(pendingEdit.bookingId, updateData);
      
      toast.success(`${pendingEdit.field === 'av_lacs' ? 'AV' : pendingEdit.field === 'token_amount' ? 'Token Amount' : 'Unit Number'} updated successfully`);
      
      // Refresh bookings
      fetchBookings();
      cancelEdit();
    } catch (error) {
      console.error('Failed to update booking:', error);
      toast.error(error.response?.data?.detail || 'Failed to update booking');
    } finally {
      setIsSaving(false);
    }
  };
  
  // Handle cost sheet upload
  const handleCostSheetUpload = async (e, bookingId) => {
    e.stopPropagation();
    const file = e.target.files?.[0];
    if (!file) return;
    
    setUploadingDoc(bookingId);
    try {
      await api.uploadExistingBookingDocument(bookingId, 'cost_sheet', file, 'Uploaded via inline edit');
      toast.success('Cost sheet uploaded successfully');
      fetchBookings();
    } catch (error) {
      console.error('Failed to upload cost sheet:', error);
      toast.error(error.response?.data?.detail || 'Failed to upload cost sheet');
    } finally {
      setUploadingDoc(null);
      if (fileInputRef.current) {
        fileInputRef.current.value = '';
      }
    }
  };
  
  // Check if user can inline edit (strategy_support_admin or super_admin)
  const canInlineEdit = role === 'admin' || role === 'city_head';
  
  // Row actions - verify booking from table
  const handleRowVerify = async (booking) => {
    setIsRowVerifying(true);
    try {
      await api.bookingVerifyAction(booking.booking_id, 'city_verify');
      toast.success('Booking verified successfully');
      fetchBookings();
    } catch (error) {
      console.error('Failed to verify booking:', error);
      toast.error(error.response?.data?.detail || 'Failed to verify booking');
    } finally {
      setIsRowVerifying(false);
    }
  };
  
  // Row actions - raise query from table
  const handleRowRaiseQuery = async () => {
    if (!rowQueryRemark.trim() || !rowActionsBooking) {
      toast.error('Please enter a query remark');
      return;
    }
    
    setIsRowSubmittingQuery(true);
    try {
      await api.bookingVerifyAction(rowActionsBooking.booking_id, 'raise_query', rowQueryRemark);
      toast.success('Query raised successfully. Rep has been notified.');
      setRowQueryRemark('');
      setShowRowRaiseQueryDialog(false);
      setRowActionsBooking(null);
      fetchBookings();
    } catch (error) {
      console.error('Failed to raise query:', error);
      toast.error(error.response?.data?.detail || 'Failed to raise query');
    } finally {
      setIsRowSubmittingQuery(false);
    }
  };
  
  // Open raise query dialog for a specific booking
  const openRowRaiseQueryDialog = (e, booking) => {
    e.stopPropagation();
    setRowActionsBooking(booking);
    setShowRowRaiseQueryDialog(true);
  };
  
  // Check if booking can have actions
  const canVerifyBooking = (booking) => ['SUBMITTED', 'QUERY_RAISED', 'Pending', 'Query Raised'].includes(booking.status);
  const canRaiseQueryOnBooking = (booking) => ['SUBMITTED', 'CITY_HEAD_VERIFIED', 'Pending'].includes(booking.status);

  // Column definitions
  const allColumns = {
    actions: { label: '', width: 'w-12' }, // Row actions column
    booking_date: { label: 'Date', width: 'w-24' },
    customer_name: { label: 'Customer', width: 'w-40' },
    project_name: { label: 'Project', width: 'w-40' },
    rep_name: { label: 'Rep', width: 'w-32' },
    manager_name: { label: 'Manager', width: 'w-32' },
    av_lacs: { label: 'AV (Lacs)', width: 'w-28', editable: true },
    token_amount: { label: 'Token (Lacs)', width: 'w-28', editable: true },
    unit_no: { label: 'Unit No', width: 'w-28', editable: true },
    booking_type: { label: 'Type', width: 'w-24' },
    documents: { label: 'Docs', width: 'w-24', editable: true },
    status: { label: 'Status', width: 'w-44' },
    action_required: { label: 'Action Required', width: 'w-36' },
    days_in_current_stage: { label: 'Days in Stage', width: 'w-28' },
    invoice_date: { label: 'Invoice Date', width: 'w-24' },
    revenue_received_date: { label: 'Revenue Date', width: 'w-24' },
  };
  
  // Toggle column visibility
  const toggleColumn = (col) => {
    setVisibleColumns(prev => 
      prev.includes(col) 
        ? prev.filter(c => c !== col)
        : [...prev, col]
    );
  };
  
  return (
    <div className="space-y-4">
      {/* Dynamic Summary Strip - Hidden for CRM Support (no sales analytics) */}
      {showSummaryStrip && !isCrmSupport && (
        <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-2">
          <div className="p-3 bg-slate-50 rounded-xl text-center border">
            <p className="text-xl font-bold text-slate-700">{summaryStats.total}</p>
            <p className="text-xs text-slate-500">Total</p>
          </div>
          <div className="p-3 bg-emerald-50 rounded-xl text-center border border-emerald-100">
            <p className="text-xl font-bold text-emerald-700">₹{summaryStats.netAv.toFixed(1)}L</p>
            <p className="text-xs text-slate-500">Net AV</p>
          </div>
          <div className="p-3 bg-blue-50 rounded-xl text-center border border-blue-100">
            <p className="text-xl font-bold text-blue-700">{summaryStats.submitted}</p>
            <p className="text-xs text-slate-500">Submitted</p>
          </div>
          {summaryStats.queryRaised > 0 && (
            <div className="p-3 bg-red-50 rounded-xl text-center border border-red-200">
              <p className="text-xl font-bold text-red-700">{summaryStats.queryRaised}</p>
              <p className="text-xs text-red-600 font-medium">Query Raised</p>
            </div>
          )}
          <div className="p-3 bg-cyan-50 rounded-xl text-center border border-cyan-100">
            <p className="text-xl font-bold text-cyan-700">{summaryStats.cityVerified}</p>
            <p className="text-xs text-slate-500">City Verified</p>
          </div>
          <div className="p-3 bg-emerald-50 rounded-xl text-center border border-emerald-100">
            <p className="text-xl font-bold text-emerald-700">{summaryStats.approved}</p>
            <p className="text-xs text-slate-500">Approved</p>
          </div>
          {summaryStats.cancelled > 0 && (
            <div className="p-3 bg-slate-50 rounded-xl text-center border">
              <p className="text-xl font-bold text-slate-500">{summaryStats.cancelled}</p>
              <p className="text-xs text-slate-500">Cancelled</p>
            </div>
          )}
        </div>
      )}
      
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-lg font-semibold text-slate-900">{title}</h2>
          <p className="text-sm text-slate-500">{total} bookings</p>
        </div>
        <div className="flex items-center gap-2">
          {/* Column Picker */}
          <div className="relative">
            <Button 
              variant="outline" 
              size="sm"
              onClick={() => setShowColumnPicker(!showColumnPicker)}
            >
              {showColumnPicker ? <EyeOff size={14} /> : <Eye size={14} />}
              <span className="ml-1">Columns</span>
            </Button>
            {showColumnPicker && (
              <div className="absolute right-0 top-full mt-1 bg-white border rounded-lg shadow-lg p-2 z-50 min-w-[160px]">
                {Object.entries(allColumns).map(([key, col]) => (
                  <label key={key} className="flex items-center gap-2 p-1.5 hover:bg-slate-50 rounded cursor-pointer">
                    <input
                      type="checkbox"
                      checked={visibleColumns.includes(key)}
                      onChange={() => toggleColumn(key)}
                      className="rounded"
                    />
                    <span className="text-sm">{col.label}</span>
                  </label>
                ))}
              </div>
            )}
          </div>
          
          {/* Export */}
          <Button variant="outline" size="sm">
            <Download size={14} />
            <span className="ml-1">Export</span>
          </Button>
        </div>
      </div>
      
      {/* Queue-based Filter Tabs */}
      {showFilters && (
        <div className="flex flex-wrap items-center gap-2 pb-2 border-b" data-testid="queue-filters">
          {QUEUE_FILTERS.map(queue => {
            const isActive = queueFilter === queue.key;
            // Count using canonical status directly
            const queueCount = queue.statuses.length > 0 
              ? bookings.filter(b => queue.statuses.includes(b.status)).length 
              : bookings.length;
            
            return (
              <Button
                key={queue.key}
                variant={isActive ? 'default' : 'outline'}
                size="sm"
                onClick={() => {
                  setQueueFilter(queue.key);
                  // Clear individual status filter when using queue filter
                  if (queue.statuses.length > 0) {
                    setStatusFilter(queue.statuses[0]); // Use first status as API filter
                  } else {
                    setStatusFilter('');
                  }
                }}
                className={isActive ? 'bg-slate-900' : ''}
                data-testid={`queue-filter-${queue.key}`}
              >
                {queue.label}
                {queueCount > 0 && (
                  <Badge variant="secondary" className="ml-2 text-xs">
                    {queueCount}
                  </Badge>
                )}
              </Button>
            );
          })}
        </div>
      )}
      
      {/* Filters */}
      {showFilters && (
        <Card className="p-4">
          <div className="flex flex-wrap items-center gap-3">
            {/* Search */}
            <div className="relative flex-1 min-w-[200px] max-w-[300px]">
              <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
              <Input
                placeholder="Search customer, booking ID..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
            
            {/* Period */}
            <Select value={period} onValueChange={setPeriod}>
              <SelectTrigger className="w-[120px]">
                <SelectValue placeholder="Period" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="today">Today</SelectItem>
                <SelectItem value="mtd">MTD</SelectItem>
                <SelectItem value="qtd">QTD</SelectItem>
                <SelectItem value="ytd">YTD</SelectItem>
                <SelectItem value="all">All Time</SelectItem>
                <SelectItem value="custom">Custom</SelectItem>
              </SelectContent>
            </Select>
            
            {/* Custom Date Range */}
            {period === 'custom' && (
              <>
                <Input
                  type="date"
                  value={startDate}
                  onChange={(e) => setStartDate(e.target.value)}
                  className="w-[140px]"
                />
                <span className="text-slate-400">to</span>
                <Input
                  type="date"
                  value={endDate}
                  onChange={(e) => setEndDate(e.target.value)}
                  className="w-[140px]"
                />
              </>
            )}
            
            {/* Status */}
            <Select value={statusFilter || "all"} onValueChange={(v) => setStatusFilter(v === "all" ? "" : v)}>
              <SelectTrigger className="w-[150px]">
                <SelectValue placeholder="All Statuses" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Statuses</SelectItem>
                {Object.entries(BOOKING_STATUSES).map(([key, val]) => (
                  <SelectItem key={key} value={key}>{val.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
            
            {/* Booking Type */}
            <Select value={bookingTypeFilter || "all"} onValueChange={(v) => setBookingTypeFilter(v === "all" ? "" : v)}>
              <SelectTrigger className="w-[130px]">
                <SelectValue placeholder="All Types" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Types</SelectItem>
                <SelectItem value="DIRECT">Direct</SelectItem>
                <SelectItem value="REFERRAL_CUSTOMER">Referral (Customer)</SelectItem>
                <SelectItem value="REFERRAL_EMPLOYEE">Referral (Employee)</SelectItem>
              </SelectContent>
            </Select>
            
            {/* AV Range */}
            <div className="flex items-center gap-1">
              <Input
                type="number"
                placeholder="Min AV"
                value={avMin}
                onChange={(e) => setAvMin(e.target.value)}
                className="w-[80px]"
              />
              <span className="text-slate-400">-</span>
              <Input
                type="number"
                placeholder="Max AV"
                value={avMax}
                onChange={(e) => setAvMax(e.target.value)}
                className="w-[80px]"
              />
            </div>
            
            {/* Clear Filters */}
            <Button 
              variant="ghost" 
              size="sm"
              onClick={() => {
                setPeriod('mtd');
                setStatusFilter('');
                setQueueFilter('all');
                setBookingTypeFilter('');
                setSearchQuery('');
                setAvMin('');
                setAvMax('');
                setManagerFilter('');
                setRepFilter('');
              }}
            >
              Clear
            </Button>
          </div>
        </Card>
      )}
      
      {/* Table */}
      <Card className="overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-slate-50 border-b">
              <tr>
                {visibleColumns.map(col => (
                  <th 
                    key={col} 
                    className={`px-4 py-3 text-left text-xs font-semibold text-slate-600 uppercase tracking-wider ${allColumns[col]?.width || ''}`}
                  >
                    {allColumns[col]?.label || col}
                  </th>
                ))}
                <th className="px-4 py-3 w-12"></th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-100">
              {loading ? (
                <tr>
                  <td colSpan={visibleColumns.length + 1} className="px-4 py-12 text-center">
                    <div className="flex items-center justify-center gap-2 text-slate-500">
                      <div className="w-5 h-5 border-2 border-slate-300 border-t-slate-600 rounded-full animate-spin" />
                      Loading...
                    </div>
                  </td>
                </tr>
              ) : bookings.length === 0 ? (
                <tr>
                  <td colSpan={visibleColumns.length + 1} className="px-4 py-12 text-center text-slate-500">
                    No bookings found
                  </td>
                </tr>
              ) : (
                bookings.map((booking, idx) => {
                  const isCancelled = booking.status?.includes('CANCELLED');
                  const isStuck = !isCancelled && booking.days_in_current_stage > (STUCK_THRESHOLDS[booking.status] || 999);
                  
                  return (
                    <tr 
                      key={booking.booking_id || idx}
                      className={`hover:bg-slate-50 cursor-pointer transition-colors ${
                        isStuck ? 'bg-amber-50/50' : ''
                      } ${isCancelled ? 'opacity-60' : ''}`}
                      onClick={() => handleRowClick(booking)}
                    >
                      {visibleColumns.map(col => (
                        <td key={col} className="px-4 py-3">
                          {col === 'actions' && canInlineEdit && (
                            <div className="relative" onClick={e => e.stopPropagation()}>
                              <div className="flex items-center gap-1">
                                {/* Quick verify button */}
                                {canVerifyBooking(booking) && (
                                  <button
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      handleRowVerify(booking);
                                    }}
                                    disabled={isRowVerifying}
                                    className="p-1.5 rounded hover:bg-emerald-50 text-emerald-600 transition-colors"
                                    title="Verify Booking"
                                    data-testid={`row-verify-${booking.booking_id}`}
                                  >
                                    {isRowVerifying ? (
                                      <Loader2 size={14} className="animate-spin" />
                                    ) : (
                                      <CheckCircle2 size={14} />
                                    )}
                                  </button>
                                )}
                                {/* Quick raise query button */}
                                {canRaiseQueryOnBooking(booking) && (
                                  <button
                                    onClick={(e) => openRowRaiseQueryDialog(e, booking)}
                                    className="p-1.5 rounded hover:bg-amber-50 text-amber-600 transition-colors"
                                    title="Raise Query"
                                    data-testid={`row-raise-query-${booking.booking_id}`}
                                  >
                                    <MessageSquare size={14} />
                                  </button>
                                )}
                                {/* View details button - opens sidebar */}
                                <button
                                  onClick={(e) => {
                                    e.stopPropagation();
                                    setSelectedBooking(booking);
                                    setDrawerOpen(true);
                                  }}
                                  className="p-1.5 rounded hover:bg-slate-100 text-slate-500 transition-colors"
                                  title="View Details"
                                >
                                  <Eye size={14} />
                                </button>
                              </div>
                            </div>
                          )}
                          {col === 'booking_date' && (
                            <span className="text-sm text-slate-700">{booking.booking_date}</span>
                          )}
                          {col === 'customer_name' && (
                            <div>
                              <p className="text-sm font-medium text-slate-900 truncate max-w-[160px]">
                                {booking.customer_name || 'N/A'}
                              </p>
                              <p className="text-xs text-slate-400 font-mono">{booking.booking_id}</p>
                            </div>
                          )}
                          {col === 'project_name' && (
                            <span className="text-sm text-slate-700 truncate block max-w-[160px]">
                              {booking.project_name}
                            </span>
                          )}
                          {col === 'rep_name' && (
                            <span className="text-sm text-slate-700">{booking.rep_name}</span>
                          )}
                          {col === 'manager_name' && (
                            <span className="text-sm text-slate-600">{booking.manager_name || 'N/A'}</span>
                          )}
                          {col === 'av_lacs' && (
                            editingCell?.bookingId === booking.booking_id && editingCell?.field === 'av_lacs' ? (
                              <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                                <Input
                                  type="number"
                                  step="0.1"
                                  value={editValue}
                                  onChange={(e) => setEditValue(e.target.value)}
                                  className="w-20 h-7 text-sm"
                                  autoFocus
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') submitEdit(e);
                                    if (e.key === 'Escape') cancelEdit(e);
                                  }}
                                />
                                <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={submitEdit}>
                                  <Check size={14} className="text-emerald-600" />
                                </Button>
                                <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={cancelEdit}>
                                  <X size={14} className="text-red-600" />
                                </Button>
                              </div>
                            ) : (
                              <div className="flex items-center gap-1 group">
                                <span className="text-sm font-semibold text-slate-900">
                                  ₹{(booking.av_lacs || 0).toFixed(1)}L
                                </span>
                                {canInlineEdit && (
                                  <button 
                                    onClick={(e) => startEdit(e, booking.booking_id, 'av_lacs', booking.av_lacs)}
                                    className="opacity-0 group-hover:opacity-100 p-1 hover:bg-slate-100 rounded transition-opacity"
                                    title="Edit AV"
                                  >
                                    <Pencil size={12} className="text-slate-400" />
                                  </button>
                                )}
                              </div>
                            )
                          )}
                          {col === 'token_amount' && (
                            editingCell?.bookingId === booking.booking_id && editingCell?.field === 'token_amount' ? (
                              <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                                <Input
                                  type="number"
                                  step="0.1"
                                  value={editValue}
                                  onChange={(e) => setEditValue(e.target.value)}
                                  className="w-20 h-7 text-sm"
                                  autoFocus
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') submitEdit(e);
                                    if (e.key === 'Escape') cancelEdit(e);
                                  }}
                                />
                                <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={submitEdit}>
                                  <Check size={14} className="text-emerald-600" />
                                </Button>
                                <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={cancelEdit}>
                                  <X size={14} className="text-red-600" />
                                </Button>
                              </div>
                            ) : (
                              <div className="flex items-center gap-1 group">
                                <span className="text-sm text-slate-700">
                                  {booking.token_amount_lacs ? `₹${booking.token_amount_lacs.toFixed(1)}L` : '-'}
                                </span>
                                {canInlineEdit && (
                                  <button 
                                    onClick={(e) => startEdit(e, booking.booking_id, 'token_amount', booking.token_amount_lacs)}
                                    className="opacity-0 group-hover:opacity-100 p-1 hover:bg-slate-100 rounded transition-opacity"
                                    title="Edit Token Amount"
                                  >
                                    <Pencil size={12} className="text-slate-400" />
                                  </button>
                                )}
                              </div>
                            )
                          )}
                          {col === 'unit_no' && (
                            editingCell?.bookingId === booking.booking_id && editingCell?.field === 'unit_no' ? (
                              <div className="flex items-center gap-1" onClick={e => e.stopPropagation()}>
                                <Input
                                  type="text"
                                  value={editValue}
                                  onChange={(e) => setEditValue(e.target.value)}
                                  className="w-24 h-7 text-sm"
                                  autoFocus
                                  onKeyDown={(e) => {
                                    if (e.key === 'Enter') submitEdit(e);
                                    if (e.key === 'Escape') cancelEdit(e);
                                  }}
                                />
                                <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={submitEdit}>
                                  <Check size={14} className="text-emerald-600" />
                                </Button>
                                <Button size="sm" variant="ghost" className="h-6 w-6 p-0" onClick={cancelEdit}>
                                  <X size={14} className="text-red-600" />
                                </Button>
                              </div>
                            ) : (
                              <div className="flex items-center gap-1 group">
                                <span className="text-sm text-slate-700">
                                  {booking.unit_no || booking.unit_number || '-'}
                                </span>
                                {canInlineEdit && (
                                  <button 
                                    onClick={(e) => startEdit(e, booking.booking_id, 'unit_no', booking.unit_no || booking.unit_number)}
                                    className="opacity-0 group-hover:opacity-100 p-1 hover:bg-slate-100 rounded transition-opacity"
                                    title="Edit Unit Number"
                                  >
                                    <Pencil size={12} className="text-slate-400" />
                                  </button>
                                )}
                              </div>
                            )
                          )}
                          {col === 'booking_type' && (
                            <Badge variant="outline" className="text-xs">
                              {booking.booking_type === 'DIRECT' ? 'Direct' : 
                               booking.booking_type === 'REFERRAL_CUSTOMER' ? 'Ref (C)' : 
                               booking.booking_type === 'REFERRAL_EMPLOYEE' ? 'Ref (E)' : 
                               booking.booking_type || 'Direct'}
                            </Badge>
                          )}
                          {col === 'documents' && (
                            <div className="flex items-center gap-1 group">
                              {booking.cost_sheet_url ? (
                                <a 
                                  href={booking.cost_sheet_url} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                  className="p-1 bg-purple-50 rounded hover:bg-purple-100"
                                  title="View Cost Sheet"
                                >
                                  <FileSpreadsheet size={12} className="text-purple-600" />
                                </a>
                              ) : canInlineEdit && (
                                <label 
                                  className="p-1 bg-slate-50 rounded hover:bg-purple-50 cursor-pointer border border-dashed border-slate-300 hover:border-purple-300 transition-colors"
                                  title="Upload Cost Sheet"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  {uploadingDoc === booking.booking_id ? (
                                    <Loader2 size={12} className="text-purple-600 animate-spin" />
                                  ) : (
                                    <Upload size={12} className="text-slate-400 hover:text-purple-600" />
                                  )}
                                  <input
                                    type="file"
                                    accept=".pdf,.jpg,.jpeg,.png"
                                    className="hidden"
                                    onChange={(e) => handleCostSheetUpload(e, booking.booking_id)}
                                    disabled={uploadingDoc === booking.booking_id}
                                  />
                                </label>
                              )}
                              {booking.booking_form_url && (
                                <a 
                                  href={booking.booking_form_url} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                  className="p-1 bg-blue-50 rounded hover:bg-blue-100"
                                  title="Booking Form"
                                >
                                  <FileText size={12} className="text-blue-600" />
                                </a>
                              )}
                              {booking.payment_proof_url && (
                                <a 
                                  href={booking.payment_proof_url} 
                                  target="_blank" 
                                  rel="noopener noreferrer"
                                  onClick={(e) => e.stopPropagation()}
                                  className="p-1 bg-emerald-50 rounded hover:bg-emerald-100"
                                  title="Payment Proof"
                                >
                                  <Paperclip size={12} className="text-emerald-600" />
                                </a>
                              )}
                              {!booking.cost_sheet_url && !booking.booking_form_url && !booking.payment_proof_url && !canInlineEdit && (
                                <span className="text-xs text-slate-400">-</span>
                              )}
                            </div>
                          )}
                          {col === 'status' && (() => {
                            const statusConfig = getStatusConfig(booking.status);
                            const isResubmitted = booking.resubmitted_at && booking.status === 'SUBMITTED';
                            const changes = booking.changes_made || [];
                            return (
                              <div className="flex flex-col gap-1">
                                <div className="flex items-center gap-2">
                                  <StatusProgressTracker 
                                    currentStatus={booking.status} 
                                    isCancelled={isCancelled} 
                                  />
                                  <Badge className={`text-xs ${statusConfig.color}`}>
                                    {statusConfig.label}
                                  </Badge>
                                </div>
                                {isResubmitted && (
                                  <TooltipProvider>
                                    <Tooltip>
                                      <TooltipTrigger asChild>
                                        <div className="flex items-center gap-1 px-2 py-0.5 bg-amber-50 border border-amber-200 rounded text-[10px] text-amber-700 cursor-help">
                                          <History size={10} />
                                          <span className="font-medium">Re-Verification</span>
                                          {booking.resubmission_count > 1 && (
                                            <span className="text-amber-500">({booking.resubmission_count}x)</span>
                                          )}
                                          <Info size={10} className="ml-1 text-amber-500" />
                                        </div>
                                      </TooltipTrigger>
                                      <TooltipContent side="bottom" className="max-w-xs p-3 bg-white border shadow-lg">
                                        <div className="space-y-2">
                                          <p className="text-xs font-semibold text-slate-700">
                                            Correction submitted by {booking.resubmitted_by_name || 'Rep'}
                                          </p>
                                          {booking.previous_query_remark && (
                                            <div className="text-[10px] text-slate-500">
                                              <span className="font-medium">Query:</span> {booking.previous_query_remark}
                                            </div>
                                          )}
                                          {booking.correction_notes && (
                                            <div className="text-[10px] text-slate-600">
                                              <span className="font-medium">Rep's Notes:</span> {booking.correction_notes}
                                            </div>
                                          )}
                                          {changes.length > 0 && (
                                            <div className="pt-1 border-t border-slate-100">
                                              <p className="text-[10px] font-semibold text-blue-600 mb-1">Changes Made:</p>
                                              {changes.slice(0, 4).map((c, idx) => (
                                                <div key={idx} className="text-[10px] text-slate-600 flex items-center gap-1">
                                                  <span className="text-slate-400">•</span>
                                                  <span className="font-medium">{c.field}:</span>
                                                  <span className="text-slate-400 line-through">{c.old_value || '-'}</span>
                                                  <span>→</span>
                                                  <span className="text-blue-600">{c.new_value || '-'}</span>
                                                </div>
                                              ))}
                                              {changes.length > 4 && (
                                                <p className="text-[10px] text-slate-400 mt-1">+{changes.length - 4} more changes</p>
                                              )}
                                            </div>
                                          )}
                                        </div>
                                      </TooltipContent>
                                    </Tooltip>
                                  </TooltipProvider>
                                )}
                              </div>
                            );
                          })()}
                          {col === 'action_required' && (() => {
                            const action = getActionRequired(booking);
                            const ActionIcon = action.icon;
                            return (
                              <div className="flex flex-col gap-1">
                                <div className={`flex items-center gap-1.5 px-2 py-1 rounded-lg border text-xs font-medium ${action.color}`}>
                                  <ActionIcon size={12} />
                                  {action.label}
                                </div>
                                {/* Low Token Alert Flag - separate from workflow status */}
                                {booking.financial_flag === 'LOW_TOKEN_ALERT' && (
                                  <div className="flex items-center gap-1 px-2 py-0.5 bg-amber-50 border border-amber-300 rounded text-[10px] text-amber-700">
                                    <AlertTriangle size={10} />
                                    <span className="font-medium">Low Token</span>
                                    {booking.token_percent !== undefined && (
                                      <span className="text-amber-500">({booking.token_percent}%)</span>
                                    )}
                                  </div>
                                )}
                              </div>
                            );
                          })()}
                          {col === 'days_in_current_stage' && (
                            <div className="flex items-center gap-2">
                              <span className={`text-sm ${isStuck ? 'text-red-600 font-semibold' : 'text-slate-700'}`}>
                                {booking.days_in_current_stage}d
                              </span>
                              <StuckIndicator status={booking.status} daysInStage={booking.days_in_current_stage} />
                            </div>
                          )}
                          {col === 'invoice_date' && (
                            <span className="text-sm text-slate-600">{booking.invoice_date || '-'}</span>
                          )}
                          {col === 'revenue_received_date' && (
                            <span className="text-sm text-slate-600">{booking.revenue_received_date || '-'}</span>
                          )}
                        </td>
                      ))}
                      <td className="px-4 py-3">
                        <Button variant="ghost" size="sm" className="h-8 w-8 p-0">
                          <MoreVertical size={16} className="text-slate-400" />
                        </Button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
        
        {/* Pagination */}
        <div className="flex items-center justify-between px-4 py-3 border-t bg-slate-50">
          <p className="text-sm text-slate-600">
            Showing {((page - 1) * pageSize) + 1} - {Math.min(page * pageSize, total)} of {total}
          </p>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.max(1, p - 1))}
              disabled={page === 1}
            >
              <ChevronLeft size={16} />
            </Button>
            <span className="text-sm text-slate-600">
              Page {page} of {totalPages}
            </span>
            <Button
              variant="outline"
              size="sm"
              onClick={() => setPage(p => Math.min(totalPages, p + 1))}
              disabled={page >= totalPages}
            >
              <ChevronRight size={16} />
            </Button>
          </div>
        </div>
      </Card>
      
      {/* Detail Drawer */}
      <BookingDetailDrawer
        booking={selectedBooking}
        isOpen={drawerOpen}
        onClose={() => setDrawerOpen(false)}
        userRole={role}
        onRefresh={fetchBookings}
      />
      
      {/* Row Action: Raise Query Dialog */}
      <Dialog open={showRowRaiseQueryDialog} onOpenChange={(open) => {
        if (!open) {
          setShowRowRaiseQueryDialog(false);
          setRowQueryRemark('');
          setRowActionsBooking(null);
        }
      }}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <MessageSquare size={20} />
              Raise Query
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-2">
            {rowActionsBooking && (
              <div className="p-3 bg-slate-50 rounded-lg border">
                <p className="text-sm text-slate-600">
                  <span className="font-medium">Booking:</span> {rowActionsBooking.booking_id}
                </p>
                <p className="text-sm text-slate-600 mt-1">
                  <span className="font-medium">Customer:</span> {rowActionsBooking.customer_name}
                </p>
                <p className="text-sm text-slate-600 mt-1">
                  <span className="font-medium">Rep:</span> {rowActionsBooking.rep_name}
                </p>
              </div>
            )}
            
            <div>
              <label className="text-sm font-medium text-slate-700">
                Query Remark <span className="text-red-500">*</span>
              </label>
              <textarea
                value={rowQueryRemark}
                onChange={(e) => setRowQueryRemark(e.target.value)}
                placeholder="Describe the issue or clarification needed..."
                className="w-full mt-1 p-3 border rounded-lg text-sm resize-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                rows={4}
                autoFocus
              />
            </div>
            
            <div className="p-3 bg-amber-50 rounded-lg border border-amber-200">
              <p className="text-xs text-amber-700">
                <strong>What happens:</strong> Status → Query Raised, Task created for rep
              </p>
            </div>
            
            <div className="flex gap-2 pt-2">
              <Button
                variant="outline"
                onClick={() => {
                  setShowRowRaiseQueryDialog(false);
                  setRowQueryRemark('');
                  setRowActionsBooking(null);
                }}
                className="flex-1"
                disabled={isRowSubmittingQuery}
              >
                Cancel
              </Button>
              <Button
                onClick={handleRowRaiseQuery}
                disabled={!rowQueryRemark.trim() || isRowSubmittingQuery}
                className="flex-1 bg-amber-600 hover:bg-amber-700"
                data-testid="row-confirm-raise-query-btn"
              >
                {isRowSubmittingQuery ? (
                  <>
                    <Loader2 size={16} className="mr-2 animate-spin" />
                    Submitting...
                  </>
                ) : (
                  <>
                    <MessageSquare size={16} className="mr-2" />
                    Raise Query
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
      
      {/* Edit Reason Dialog */}
      <Dialog open={showEditReasonDialog} onOpenChange={(open) => !open && cancelEdit()}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Pencil size={18} className="text-blue-600" />
              Confirm Edit
            </DialogTitle>
          </DialogHeader>
          
          <div className="space-y-4 mt-2">
            {pendingEdit && (
              <div className="p-3 bg-slate-50 rounded-lg border">
                <p className="text-sm text-slate-600">
                  <span className="font-medium">Field:</span> {
                    pendingEdit.field === 'av_lacs' ? 'Agreement Value' :
                    pendingEdit.field === 'token_amount' ? 'Token Amount' :
                    pendingEdit.field === 'unit_no' ? 'Unit Number' : pendingEdit.field
                  }
                </p>
                <p className="text-sm text-slate-600 mt-1">
                  <span className="font-medium">Old Value:</span> {pendingEdit.oldValue || '-'}
                </p>
                <p className="text-sm text-slate-600 mt-1">
                  <span className="font-medium">New Value:</span> {pendingEdit.value}
                </p>
              </div>
            )}
            
            <div>
              <label className="text-sm font-medium text-slate-700">
                Reason for Edit <span className="text-red-500">*</span>
              </label>
              <Input
                value={editReason}
                onChange={(e) => setEditReason(e.target.value)}
                placeholder="e.g., Corrected as per builder confirmation"
                className="mt-1"
                autoFocus
              />
              <p className="text-xs text-slate-400 mt-1">This will be logged for audit purposes</p>
            </div>
            
            <div className="flex gap-2 pt-2">
              <Button
                variant="outline"
                onClick={cancelEdit}
                className="flex-1"
                disabled={isSaving}
              >
                Cancel
              </Button>
              <Button
                onClick={confirmEditWithReason}
                disabled={!editReason.trim() || isSaving}
                className="flex-1 bg-blue-600 hover:bg-blue-700"
              >
                {isSaving ? (
                  <>
                    <Loader2 size={16} className="mr-2 animate-spin" />
                    Saving...
                  </>
                ) : (
                  <>
                    <Check size={16} className="mr-2" />
                    Confirm Edit
                  </>
                )}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default UnifiedBookingTable;
